import { useRef, type KeyboardEvent } from 'react';
import { Check } from 'lucide-react';
import { SettingsCard, SectionHead } from '@components/settings/chrome';
import { Segmented } from '@shared/ui/kit';
import { useThemeStore } from '@stores/themeStore';
import { THEMES, type ThemeMode } from '@/theme';

// Settings -> Set Theme.
//
// Two choices, deliberately shown as two sections rather than one list: WHICH
// theme the product wears, and whether it is light or dark. They are
// orthogonal — every theme ships both appearances — and flattening them into
// four options would imply they are alternatives to one another.
//
// This screen holds no theme knowledge of its own. It renders whatever
// `THEMES` contains, so a third theme appears here the moment it is added to
// the registry.

const APPEARANCE_OPTIONS = [
  { id: 'light', label: 'Light' },
  { id: 'dark', label: 'Dark' },
  { id: 'system', label: 'System' },
] as const;

/**
 * A theme's two palettes side by side, drawn from its own hexes.
 *
 * Literal colours are correct here and nowhere else: the tile advertises a
 * theme that is NOT applied, so it cannot read the live tokens. They come from
 * the theme definition rather than being typed in, so a tile can never drift
 * from the theme it is selling.
 */
function ThemePreview({
  swatches,
  mode,
}: {
  swatches: readonly [string, string, string, string];
  mode: ThemeMode;
}) {
  const [canvas, surface, accent, ink] = swatches;

  return (
    <div
      className="min-w-0 flex-1 overflow-hidden rounded-md border"
      style={{ background: canvas, borderColor: 'var(--co-line)' }}
      aria-hidden="true"
    >
      {/* A header band, then a card with two lines and a button — enough shape
          to read as a screen rather than as a strip of colour chips. */}
      <div className="flex items-center gap-[5px] px-[9px] py-[7px]">
        <span className="h-[7px] w-[7px] flex-none rounded-full" style={{ background: accent }} />
        <span className="h-[4px] w-[34px] rounded-full" style={{ background: ink, opacity: 0.85 }} />
        <span
          className="co-mono ml-auto text-[7px] uppercase tracking-[.16em]"
          style={{ color: ink, opacity: 0.55 }}
        >
          {mode}
        </span>
      </div>
      <div className="px-[9px] pb-[9px]">
        <div
          className="space-y-[6px] rounded-md border p-[8px]"
          style={{ background: surface, borderColor: `${ink}1f` }}
        >
          <span className="block h-[4px] w-[70%] rounded-full" style={{ background: ink, opacity: 0.7 }} />
          <span className="block h-[4px] w-[45%] rounded-full" style={{ background: ink, opacity: 0.32 }} />
          <span className="block h-[10px] w-[38px] rounded" style={{ background: accent }} />
        </div>
      </div>
    </div>
  );
}

export default function AppearanceSettings() {
  const theme = useThemeStore((s) => s.theme);
  const appTheme = useThemeStore((s) => s.appTheme);
  const setTheme = useThemeStore((s) => s.setTheme);
  const setAppTheme = useThemeStore((s) => s.setAppTheme);

  const optionRefs = useRef<(HTMLButtonElement | null)[]>([]);

  /**
   * Arrow keys move between themes, as a radiogroup is expected to.
   *
   * This is what earns the roving tabindex below: without it, Tab would reach
   * only the selected option and the rest would be unreachable by keyboard.
   */
  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const step =
      event.key === 'ArrowRight' || event.key === 'ArrowDown'
        ? 1
        : event.key === 'ArrowLeft' || event.key === 'ArrowUp'
          ? -1
          : 0;
    if (!step) return;

    event.preventDefault();
    const index = THEMES.findIndex((t) => t.id === appTheme);
    const next = THEMES[(index + step + THEMES.length) % THEMES.length];
    setAppTheme(next.id);
    optionRefs.current[THEMES.indexOf(next)]?.focus();
  };

  return (
    <div className="space-y-[14px]">
      <SettingsCard>
        <SectionHead
          title="Set Theme"
          description="Choose how CentraOne should look. The change applies everywhere immediately and is remembered on this browser."
        />

        <div
          role="radiogroup"
          aria-label="Application theme"
          onKeyDown={onKeyDown}
          className="mt-5 grid gap-[14px] sm:grid-cols-2"
        >
          {THEMES.map((definition, index) => {
            const selected = definition.id === appTheme;

            return (
              <button
                key={definition.id}
                type="button"
                role="radio"
                aria-checked={selected}
                tabIndex={selected ? 0 : -1}
                ref={(node) => {
                  optionRefs.current[index] = node;
                }}
                onClick={() => setAppTheme(definition.id)}
                className="group flex flex-col gap-[11px] rounded-lg border text-left transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--co-brass)]"
                style={{
                  background: selected ? 'var(--co-brass-wash)' : 'var(--co-raised)',
                  // Selection is the border AND the tick AND the weight, never
                  // a wash alone: a tinted ground is invisible to anyone who
                  // cannot separate the two hues. The padding compensates for
                  // the thicker border so the tile does not shift on select.
                  borderColor: selected ? 'var(--co-brass)' : 'var(--co-line)',
                  borderWidth: selected ? 2 : 1,
                  padding: selected ? 12 : 13,
                }}
              >
                <div className="flex gap-[9px]">
                  <ThemePreview swatches={definition.preview.light} mode="light" />
                  <ThemePreview swatches={definition.preview.dark} mode="dark" />
                </div>

                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <span
                      className="block text-[13.5px] leading-[1.2] text-[var(--co-ink)]"
                      style={{ fontWeight: selected ? 700 : 600 }}
                    >
                      {definition.name}
                    </span>
                    <span className="mt-[3px] block text-[12px] leading-[1.45] text-[var(--co-ink-3)]">
                      {definition.description}
                    </span>
                  </div>

                  {selected ? (
                    <span className="co-mono flex flex-none items-center gap-[5px] text-[9px] font-semibold uppercase tracking-[.16em] text-[var(--co-brass-ink)]">
                      <Check className="h-[12px] w-[12px]" aria-hidden="true" />
                      Selected
                    </span>
                  ) : (
                    <span
                      className="h-[13px] w-[13px] flex-none rounded-full border border-[var(--co-line-strong)]"
                      aria-hidden="true"
                    />
                  )}
                </div>
              </button>
            );
          })}
        </div>
      </SettingsCard>

      <SettingsCard>
        <SectionHead
          title="Appearance"
          description="Light or dark, for whichever theme is selected above. System follows your operating system setting and changes with it."
        />

        <div className="mt-4 flex flex-wrap items-center gap-4">
          <Segmented
            options={APPEARANCE_OPTIONS}
            active={theme}
            onSelect={setTheme}
            label="Appearance"
          />
          <span className="co-mono text-[9.5px] uppercase tracking-[.16em] text-[var(--co-ink-3)]">
            {theme === 'system' ? 'Following your system setting' : `Always ${theme}`}
          </span>
        </div>
      </SettingsCard>
    </div>
  );
}
